import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import React, { useMemo } from 'react'
import Footer from 'src/components/Footer'
import LandingNav from 'src/components/LandingNav'
import MobileStickyCta from 'src/components/MobileStickyCta'
import WhatsAppButton from 'src/components/WhatsappButton'
import { PillarPage } from 'src/constants/searchPages'
import useUserLocation from 'src/hooks/useUserLocation'
import Logos from 'src/screens/Landing/Logos'
import {
  getOnDemandHeroImage,
  OnDemandCityConfig,
  resolveOnDemandCityByCoords
} from 'src/utils/onDemand'

import CoverageZones from './CoverageZones'
import FaqsOnDemand from './FaqsOnDemand'
import FourStepsProcess from './FourStepsProcess'
import HeroOnDemand from './HeroOnDemand'
import IndicativeSizesGrid, { SIZE_TO_M2 } from './IndicativeSizesGrid'
import OnDemandPhotoBreak from './OnDemandPhotoBreak'
import {
  DurationTiers,
  NotInstantStrip,
  RestrictedItems
} from './PartnerLearningSections'
import PillarCrossLink from './PillarCrossLink'
import ThreePillars from './ThreePillars'
import TrustSignals from './TrustSignals'
import { buildOnDemandJsonLd, IBreadcrumbItem } from './jsonLd'

const SITE_ORIGIN = 'https://www.spotme.mx'
const PARENT_PATH = `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio`

type IProps =
  | {
      variant: 'parent'
      activeCities: OnDemandCityConfig[]
    }
  | {
      variant: 'city'
      coverage: OnDemandCityConfig
    }

/**
 * Almacenamiento a Domicilio screen — composes all sections and emits JSON-LD
 * (Service + LocalBusiness + BreadcrumbList) for both the generic parent page
 * and per-city pages.
 *
 * The lead-capture surface (`OnDemandWizard`, mounted inside
 * `HeroOnDemand` on the right rail) wires up the `submitOnDemandLead`
 * GraphQL mutation, fires GA4/Meta/UET ADD_TO_CART pixels
 * (`subType: 'on_demand'`), and includes a hidden honeypot anti-spam
 * field on its final step. The wizard container carries `id="cotizar"`
 * so the hero primary CTA, the mobile sticky CTA, and any deep links to
 * `#cotizar` all scroll to step 1 of the wizard.
 *
 * `<PillarCrossLink>` sits at the bottom of the page, just above the Footer,
 * as an exit ramp to the traditional SelfStorage pillar for users who'd
 * rather rent a physical mini-bodega than use door-to-door. On city pages
 * it includes a curated grid of hood / satellite-city chips so users can
 * drill straight to their area.
 */
const AlmacenamientoADomicilio = (props: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const isCity = props.variant === 'city'
  const coverage = isCity ? props.coverage : undefined

  const cityDisplayName = coverage?.displayName
  const pageUrl = isCity
    ? `${SITE_ORIGIN}${PARENT_PATH}/${coverage!.canonicalSlug}`
    : `${SITE_ORIGIN}${PARENT_PATH}`

  const serviceName = t('JSON_LD.SERVICE_NAME')
  const serviceDescription = isCity
    ? t('JSON_LD.SERVICE_DESCRIPTION_CITY', { city: cityDisplayName })
    : t('JSON_LD.SERVICE_DESCRIPTION')

  const breadcrumbTrail: IBreadcrumbItem[] = [
    { name: t('BREADCRUMBS.HOME'), url: `${SITE_ORIGIN}/` },
    {
      name: t('BREADCRUMBS.PILLAR'),
      url: `${SITE_ORIGIN}/${PillarPage.SelfStorage}`
    },
    {
      name: t('BREADCRUMBS.ON_DEMAND'),
      url: `${SITE_ORIGIN}${PARENT_PATH}`
    },
    ...(isCity
      ? [
          {
            name: cityDisplayName!,
            url: pageUrl
          }
        ]
      : [])
  ]

  // Pull FAQ items + 4-step process out of the on-demand namespace for the
  // FAQPage and HowTo rich-result schemas. Same locale data the visible
  // sections render — keeps SERP markup in lockstep with on-page copy
  // (Google penalizes drift between visible and structured content).
  const rawFaqItems = t('FAQS.ITEMS', { returnObjects: true }) as unknown as
    | { QUESTION: string; ANSWER: string }[]
    | string
  const faqItems = Array.isArray(rawFaqItems)
    ? rawFaqItems.map(({ QUESTION, ANSWER }) => ({
        question: QUESTION,
        answer: ANSWER
      }))
    : []

  const howToSteps = (['STEP_1', 'STEP_2', 'STEP_3', 'STEP_4'] as const).map(
    (key) => ({
      name: t(`PROCESS.${key}.TITLE`),
      text: t(`PROCESS.${key}.BODY`)
    })
  )

  // Pricing anchor for the Service `offers` (AggregateOffer) block — derived
  // from the SAME numbers the visible size grid renders (per-m² rate × the
  // tier's estimated m²), so the structured data can never quote a range the
  // page doesn't show: low = smallest size on the cheapest (12+ month) rate,
  // high = largest size month-to-month. City page → its coverage; parent →
  // the first active city, matching the grid's SSR fallback.
  const pricingSource: OnDemandCityConfig | undefined =
    props.variant === 'city' ? props.coverage : props.activeCities[0]
  const offers = pricingSource
    ? {
        lowPrice: pricingSource.perSqmRates.twelveMonths * SIZE_TO_M2.SMALL,
        highPrice: pricingSource.perSqmRates.monthToMonth * SIZE_TO_M2.LARGE,
        offerCount: 3
      }
    : undefined

  const jsonLdBlocks = buildOnDemandJsonLd({
    serviceName,
    serviceDescription,
    pageUrl,
    city: coverage?.canonicalSlug,
    displayName: cityDisplayName,
    coverageZones: coverage?.coverageZones,
    breadcrumbTrail,
    offers,
    faqItems,
    howTo: {
      name: t('PROCESS.TITLE'),
      description: t('HERO.SUBHEAD'),
      steps: howToSteps
    }
  })

  // Light geolocation personalization on the PARENT variant (no-op on city
  // pages — the URL already pins a city). Resolves the user's current
  // coords (browser geo with prior permission > IP geo > last-known) to
  // the closest covered, partner-active on-demand market. When matched,
  // the price tile in the hero anchors against THAT city's pricing and
  // both lead-capture forms pre-select THAT city's pickup option. All
  // silent — no copy swap, no breadcrumb shift, no layout reflow beyond
  // a numeric value change in the price tile. SSR ships the
  // first-active-city fallback, hydration optionally swaps in the
  // personalized one once geolocation resolves.
  const { center: userCenter } = useUserLocation()
  const personalizedCity: OnDemandCityConfig | undefined = useMemo(() => {
    if (props.variant !== 'parent') return undefined
    if (!userCenter) return undefined
    const resolved = resolveOnDemandCityByCoords(userCenter.lat, userCenter.lng)
    if (!resolved || !resolved.config.partnerActive) return undefined
    // Defensive: only return cities that the page is actually advertising
    // as active. Prevents a state where the coverage map flips a city
    // active mid-deploy and the SSR shipped without it.
    return props.activeCities.find(
      (c) => c.canonicalSlug === resolved.config.canonicalSlug
    )
  }, [props, userCenter])

  // Fallback coverage for the parent page — prefer the personalized city
  // when geolocation resolves to a covered market, otherwise fall back to
  // the first active city so the grid always shows concrete numbers and
  // the duration toggle uses real per-m² rates. Per-m² rates are aligned
  // across cities today, so this is mostly a future-proofing seam (lets
  // cities diverge without breaking the size tiles).
  const fallbackCoverage =
    props.variant === 'parent'
      ? (personalizedCity ?? props.activeCities[0])
      : undefined

  return (
    <>
      <Head>
        {jsonLdBlocks.map((block, idx) => (
          <script
            key={`jsonld-${idx}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
          />
        ))}
      </Head>

      <LandingNav />

      <main>
        <HeroOnDemand
          cityDisplayName={cityDisplayName}
          coverage={coverage}
          activeCities={
            props.variant === 'parent' ? props.activeCities : undefined
          }
          personalizedCity={personalizedCity}
        />
        <Logos band="demand" />
        <ThreePillars />
        <FourStepsProcess />
        <OnDemandPhotoBreak coverage={coverage} />
        <NotInstantStrip />
        <IndicativeSizesGrid
          coverage={coverage}
          fallbackCoverage={fallbackCoverage}
        />
        <DurationTiers
          coverage={coverage}
          fallbackCoverage={fallbackCoverage}
        />
        <TrustSignals />
        <CoverageZones coverage={coverage} />
        <FaqsOnDemand />
        <RestrictedItems />
        {/* The lead-capture surface now lives exclusively in the hero
            (`<OnDemandWizard>` mounted by `HeroOnDemand`). The standalone
            `<OnDemandLeadForm>` section that used to sit here was removed
            when the wizard absorbed both legacy forms — see
            `OnDemandWizard/index.tsx` for the rationale. Anchors and
            sticky CTAs still scroll to `#cotizar`, which is now the
            wizard container in the hero. */}

        {props.variant === 'parent' && props.activeCities.length > 0 && (
          <section
            aria-labelledby="city-picker-heading"
            className="border-t border-ink-100/15 bg-cream-300 py-20 md:py-28"
          >
            <div className="mx-auto max-w-7xl px-5 md:px-10">
              <h2
                id="city-picker-heading"
                className="font-display text-[26px] leading-[1.15] font-medium tracking-[-0.01em] text-ink-700 md:text-[36px]"
              >
                {t('PARENT_PAGE.CITY_PICKER_TITLE')}
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-200 md:text-base">
                {t('PARENT_PAGE.CITY_PICKER_SUBTITLE')}
              </p>
              {/* Photo tiles — each city's own hero shot (MTY skyline,
                  CDMX loading bay) so the picker previews the page it
                  links to. Two active markets today, hence two columns;
                  a third wraps onto its own row. */}
              <ul className="mt-8 grid gap-4 md:mt-10 md:grid-cols-2 md:gap-6">
                {props.activeCities.map((city) => (
                  <li key={city.canonicalSlug}>
                    <Link
                      href={`${PARENT_PATH}/${city.canonicalSlug}`}
                      className="group relative block aspect-16/10 overflow-hidden rounded-lg bg-ink-800 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-500"
                    >
                      <Image
                        src={getOnDemandHeroImage(city.canonicalSlug)}
                        alt=""
                        fill
                        sizes="(max-width: 899px) 100vw, 50vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-800/85 via-ink-800/25 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 md:p-7">
                        <div className="min-w-0">
                          <span className="block font-display text-[28px] leading-none font-medium tracking-[-0.01em] text-white-500 md:text-[36px]">
                            {city.displayShortName}
                          </span>
                          {/* Only when it adds something — Monterrey's
                              full and short names are the same string. */}
                          {city.displayName !== city.displayShortName && (
                            <span className="mt-2 block truncate text-[13px] text-white-500/80 md:text-sm">
                              {city.displayName}
                            </span>
                          )}
                        </div>
                        <span
                          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white-500 transition-transform group-hover:translate-x-0.5 md:size-11"
                          aria-hidden="true"
                        >
                          <i className="si si-right text-[11px]" />
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Exit ramp to traditional self-storage. Sits at the bottom of the
            page, after every conversion-oriented section, so users who
            scrolled this far without engaging the lead form get a clear
            alternative path. On city pages: city pillar CTA + curated hood
            chips. On the parent page: pillar-wide CTA. */}
        <PillarCrossLink coverage={coverage} />
      </main>

      <Footer />
      {/* WhatsApp FAB pushed up to `bottom-24` on mobile so it clears the
          MobileStickyCta sticky bar (~70px tall at `bottom-0`). Desktop
          position unchanged (the bar is `md:hidden`). */}
      <WhatsAppButton
        revealAt={150}
        mobileBottom="bottom-24"
        msg={
          cityDisplayName
            ? t('HERO.WHATSAPP_MSG_CITY', { city: cityDisplayName })
            : t('HERO.WHATSAPP_MSG')
        }
      />
      {/* Sticky bar reveals after the user scrolls past the wizard, which
          sits in the hero (`#cotizar`). Two-edge form check in the shared
          component handles the form-in-hero layout correctly. */}
      <MobileStickyCta label={t('HERO.PRIMARY_CTA')} formId="cotizar" />
    </>
  )
}

export default React.memo(AlmacenamientoADomicilio)
